<template>
	<div v-if="slides.length > 0" class="advertisement-carousel" :class="{ compact, 'single-slide': isSingleSlide, 'with-arrows': showArrows }">
		<VueSlickCarousel ref="carousel" v-bind="carouselSettings">
			<div v-for="(slide, index) in slides" :key="index" class="advertisement-slide">
				<AdvertisementBanner :advertisement="slide" :compact="compact" :height="slideHeight" :preview="preview" />
			</div>
		</VueSlickCarousel>
		<a v-if="showArrows" role="button" class="advertisement-arrow" @click="$refs.carousel.prev()">
			<i class="fas fa-angle-left"></i>
		</a>
		<a v-if="showArrows" role="button" class="advertisement-arrow right" @click="$refs.carousel.next()">
			<i class="fas fa-angle-right"></i>
		</a>
	</div>
</template>

<script>
import VueSlickCarousel from "vue-slick-carousel";
import "vue-slick-carousel/dist/vue-slick-carousel.css";
import AdvertisementBanner from "./AdvertisementBanner.vue";
import { DEFAULT_AUTOPLAY_SPEED, DEFAULT_SLIDES_TO_SHOW } from "../../constants/advertisement";

export default {
	name: "AdvertisementCarousel",
	components: { VueSlickCarousel, AdvertisementBanner },
	props: {
		productVariantId: { type: String, default: null },
		compact: { type: Boolean, default: false },
		advertisements: { type: Object, default: () => ({}) },
		preview: { type: Boolean, default: false },
	},
	computed: {
		// Student Dashboard shows every active ad not hidden from it; a course page shows ads targeting that batch
		slides() {
			const slides = Array.isArray(this.advertisements.slides) ? this.advertisements.slides.filter((slide) => String(slide.isActive).toLowerCase() !== "false") : [];
			if (!this.productVariantId) {
				return slides.filter((slide) => String(slide.showOnStudentDashboard).toLowerCase() !== "false");
			}

			return slides.filter((slide) => [].concat(slide.productVariantIds || []).map(String).includes(this.productVariantId));
		},
		slideHeight() {
			return this.compact ? this.advertisements.compactHeight : this.advertisements.height;
		},
		isSingleSlide() {
			return this.visibleSlides === 1;
		},
		visibleSlides() {
			if (this.compact) {
				return 1;
			}

			const slidesToShow = Number(this.advertisements.slidesToShow);

			return slidesToShow > 0 ? slidesToShow : DEFAULT_SLIDES_TO_SHOW;
		},
		autoplaySpeed() {
			const autoplaySpeed = Number(this.advertisements.autoplaySpeed);

			return autoplaySpeed > 0 ? autoplaySpeed : DEFAULT_AUTOPLAY_SPEED;
		},
		showArrows() {
			return this.slides.length > this.visibleSlides;
		},
		carouselSettings() {
			return {
				dots: false,
				speed: 500,
				arrows: false,
				infinite: this.showArrows,
				autoplay: String(this.advertisements.autoplay).toLowerCase() !== "false",
				autoplaySpeed: this.autoplaySpeed,
				pauseOnHover: true,
				adaptiveHeight: this.isSingleSlide,
				slidesToShow: this.visibleSlides,
				slidesToScroll: 1,
				responsive: [
					{ breakpoint: 1200, settings: { slidesToShow: Math.min(this.visibleSlides, 2) } },
					{ breakpoint: 768, settings: { slidesToShow: 1 } },
				],
			};
		},
	},
};
</script>

<style scoped>
.advertisement-carousel {
	position: relative;
	width: 100%;
	margin: 1rem 0 2rem;
}
.advertisement-carousel:not(.single-slide) ::v-deep .slick-track {
	display: flex;
}
.advertisement-carousel ::v-deep .slick-list {
	padding-bottom: 2px;
}
.advertisement-carousel ::v-deep .slick-slide {
	height: auto;
}
.advertisement-carousel ::v-deep .slick-slide > div {
	height: 100%;
}
.advertisement-slide {
	height: 100%;
	padding: 0 0.5rem;
}
.advertisement-arrow {
	position: absolute;
	top: 50%;
	left: -1.25rem;
	transform: translateY(-50%);
	width: 2.5rem;
	height: 2.5rem;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 1px solid #231f20;
	border-radius: 50%;
	background-color: var(--color-white);
	color: #231f20;
	font-size: 1.5rem;
	line-height: 1;
	text-decoration: none;
	z-index: 1;
}
.advertisement-arrow.right {
	left: auto;
	right: -1.25rem;
}
.advertisement-arrow:hover {
	background-color: #231f20;
	color: var(--color-white);
}

.advertisement-carousel.compact {
	margin: 1rem 0 1.5rem;
}
/* Compact arrows sit in a strip above the card */
.advertisement-carousel.compact.with-arrows {
	padding-top: 2.25rem;
}
.advertisement-carousel.compact .advertisement-slide {
	padding: 0;
}
.advertisement-carousel.compact .advertisement-arrow {
	top: 0;
	left: auto;
	right: 2.25rem;
	transform: none;
	width: 1.75rem;
	height: 1.75rem;
	font-size: 1.25rem;
}
.advertisement-carousel.compact .advertisement-arrow.right {
	right: 0;
}
</style>
